/* eslint-disable react-compiler/react-compiler */
import Form from '@rjsf/core'
import {
  RegistryFieldsType,
  RegistryWidgetsType,
  RJSFValidationError,
} from '@rjsf/utils'
import validator from '@rjsf/validator-ajv8'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { CorrosionClassificationField } from 'features/Corrosion/CorrosionClassificationField'
import { merge } from 'lodash'
import { AdditionalWorkingStepFormConfiguration } from 'model/AdditionalWorkingStep/AdditionalWorkingStepForm'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { WorkingStepType } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useShowException } from 'utils/useShowException'
import { WorkingStepFormValidator } from './WorkingStepFormValidator'
import { ArrayFieldTemplate } from './rjsf/Templates/ArrayFieldTemplate'
import { FieldTemplate } from './rjsf/Templates/FieldTemplate'
import { ObjectFieldTemplate } from './rjsf/Templates/ObjectFieldTemplate'
import { RJSFQuantityField } from './rjsf/Templates/RJSFQuantityField'
import { RJSFBaseInputTemplate } from './rjsf/Templates/RJSFTextField'
import { RalSelectorField } from './rjsf/Templates/RalSelectorField'
import { CheckboxField } from './rjsf/Widgets/CheckboxField'
import { SelectWidget } from './rjsf/Widgets/SelectWidget'
import { ZincCodeWidget } from './rjsf/Widgets/ZincCodeWidget'
import { WorkingStepWithData } from './types/WorkingStepWithData'

export type NewWorkingStepFormRef = {
  refreshData: () => Promise<void>
  getValidations: () => boolean
}

const customFields: RegistryFieldsType = {
  quantityField: RJSFQuantityField as never,
  ralSelectorField: RalSelectorField as never,
  corrosionClassificationField: CorrosionClassificationField as never,
  zincCode: ZincCodeWidget as never,
  BooleanField: CheckboxField,
}

const customWidgets: RegistryWidgetsType = {
  SelectWidget: SelectWidget,
}

const templates = {
  FieldTemplate: FieldTemplate,
  ArrayFieldTemplate: ArrayFieldTemplate,
  ObjectFieldTemplate: ObjectFieldTemplate,
  ButtonTemplates: {
    SubmitButton: () => null,
  },
  FieldErrorTemplate: () => null,
  BaseInputTemplate: RJSFBaseInputTemplate,
}

type Props = {
  ref: React.RefObject<NewWorkingStepFormRef>
  bomItemPointer: BomItemPointer
  workingStep: WorkingStepWithData
  onChange: <T extends keyof WorkingStepWithData>(
    field: T,
    value: WorkingStepWithData[T],
  ) => void
  onError: (errros: RJSFValidationError[]) => void
  projectId: string
  controller: WorkingStepsController
  loading: Record<string, boolean>
  onValidationError?: (
    workingStepWithData: WorkingStepWithData,
    errors: Record<string, { __errors: string[] }>,
  ) => void
  removeValidations?: (
    primaryWorkingStep: WorkingStepType,
    itemId: string,
  ) => void
}

export const WorkingStepForm = (props: Props) => {
  const [form, setForm] =
    useState<AdditionalWorkingStepFormConfiguration | null>(null)
  const { projectId, controller, loading } = props
  const ShowException = useShowException('working step form')

  const getWorkingStepDetails = useCallback(async () => {
    try {
      return await controller.GetWorkingStepForm(
        projectId,
        props.bomItemPointer,
        props.workingStep.bomItemActivityId,
        props.workingStep.secondaryWorkingStep,
      )
    } catch (err) {
      ShowException(err)
      return null
    }
  }, [
    ShowException,
    controller,
    projectId,
    props.bomItemPointer,
    props.workingStep.bomItemActivityId,
    props.workingStep.secondaryWorkingStep,
  ])

  const formRef = useRef<Form>(null)

  useImperativeHandle(props.ref, () => ({
    refreshData: async () => {
      const updatedForm = await getWorkingStepDetails()
      setForm(updatedForm)
      setTimeout(() => {
        formRef.current?.validateForm()
      }, 100)
    },
    getValidations: () => {
      return formRef.current?.validateForm()
    },
  }))

  useEffect(() => {
    getWorkingStepDetails().then((serverData) => {
      if (!serverData) {
        setForm(null)
        return
      }

      if (props.workingStep?.formData) {
        const mergedFormData = merge(
          props.workingStep.formData,
          serverData.formData,
        )
        serverData.formData = mergedFormData
      }
      setForm(serverData)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getWorkingStepDetails])

  if (!props.workingStep) {
    return null
  }

  if (!form) {
    return null
  }

  if (form?.jsonSchema?.title === 'EmptyDto') {
    return null
  }

  const { onChange } = props

  return (
    <div className={'ws__form__wrapper'}>
      <Form
        liveValidate={true}
        showErrorList={false}
        templates={templates}
        fields={customFields}
        widgets={customWidgets}
        ref={formRef}
        schema={form.jsonSchema}
        uiSchema={form.uiSchema}
        formData={form.formData}
        onChange={(data) => {
          if (Object.values(data.formData || {}).length > 0) {
            onChange('formData', data.formData)
          }
        }}
        validator={validator}
        onError={props.onError}
        customValidate={WorkingStepFormValidator as any}
      />
      {/* <JsonViewer data={form} /> */}
    </div>
  )
}
